import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach.js';

import {
  flowTokensStore,
  type FlowTokensStoreState,
  type FlowTokenProps,
} from './FlowTokensStore.ts';

export function useFlowTokensData() {
  useAttach(flowTokensStore, 'useFlowTokensData');
  return useStoreWithEqualityFn(flowTokensStore.store, selectData, shallow);
}
useFlowTokensData.store = flowTokensStore;

export function useFlowTokensById() {
  useAttach(flowTokensStore, 'useFlowTokensById');
  return useStoreWithEqualityFn(flowTokensStore.store, selectById, shallow);
}
useFlowTokensById.store = flowTokensStore;

export function useFlowTokensByOwnerUri() {
  const flowTokens = useFlowTokensById();

  return useMemo(() => {
    if (flowTokens.byId == null) {
      return {};
    }

    const result: Record<string, Record<string, FlowTokenProps>> = {};

    for (const flowToken of Object.values(flowTokens.byId)) {
      result[flowToken.ownerUri] = result[flowToken.ownerUri] ?? {};
      result[flowToken.ownerUri][flowToken.id] = flowToken;
    }

    return {
      byOwnerUri: result,
      isLoading: flowTokens.isLoading,
      error: flowTokens.error,
    };
  }, [flowTokens.byId, flowTokens.isLoading, flowTokens.error]);
}

export function useFlowToken({ flowTokenId }: { flowTokenId: string }) {
  useAttach(flowTokensStore, 'useFlowToken');

  const select = useCallback(
    (state: FlowTokensStoreState) => {
      return {
        flowToken: state.byId?.[flowTokenId] ?? null,
        flowTokenInstance: state.data?.[flowTokenId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [flowTokenId],
  );

  return useStoreWithEqualityFn(flowTokensStore.store, select, shallow);
}
useFlowToken.store = flowTokensStore;

export function useFlowTokensAttach() {
  useAttach(flowTokensStore, 'useFlowTokensAttach');
}
useFlowTokensAttach.store = flowTokensStore;

export function useFlowTokensLoading() {
  useAttach(flowTokensStore, 'useFlowTokensLoading');
  return useStoreWithEqualityFn(flowTokensStore.store, selectIsLoading, shallow);
}
useFlowTokensLoading.store = flowTokensStore;
